import "core-js/modules/es.array.push.js";
import UserApi from 'app/api/UserApi';
import { resolveApiErrorMessage, useApiErrorToast } from 'app/api/app-api/apiHooksHelpers';
import { validateTransfer } from 'app/modules/organizations/api/organizationsApi';
import { LoadingSpinner } from "../../core/components/LoadingSpinner";
import { UserStatusDot } from "../../core/components/StatusDot/ContractStatusDot";
import { constantToDisplayName } from "../../core/constantToDisplayName";
import { UserRole } from 'legoland-sdk/dist/experimental';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTable } from 'react-table';
import styled, { css } from 'styled-components';
import { Alert, Box, Button, Checkbox, FormGroup, Input, Label, ModalActions, ModalHeader, ModalLayout, Select, Text, ModalContent as TombacModalContent, propStyling, tombac, useToasts } from 'tombac';
import { AcceptIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon, SearchIcon } from 'tombac-icons';
import isEmail from 'validator/lib/isEmail';
import { Pagination } from "../../contracts/list/Pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "../../contracts/list/Table";
import { InviteToOrganizationModal } from "../../organizations/components/InviteToOrganizationModal";
import { useContractSuggestedUserListQuery } from "../../users/list/usersApiHooks";
import { useResolveContractOverlaps, useSetContractUsersMutation } from "../contractsApiHooks";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AssignContractUsers = _ref => {
  let {
    contract,
    onCancel,
    onAssignSuccess,
    userRole
  } = _ref;
  const EMPTY_NEW_USER = {
    email: '',
    role: userRole === UserRole.Distributor ? UserRole.User : ''
  };
  const [page, setPage] = useState({
    index: 0,
    size: 50
  });
  const [filters, setFilters] = useState({
    searchValues: '',
    organizationId: contract.organization.id
  });
  const {
    addToast
  } = useToasts();
  const addApiErrorToast = useApiErrorToast();
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [newUsersList, setNewUsersList] = useState([]);
  const [userErrors, setUserErrors] = useState([]);
  const [showUserErrors, setShowUserErrors] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const {
    mutateAsync: getUsersContractOverlaps
  } = useResolveContractOverlaps();
  const userRoles = Object.keys(UserRole).map(k => ({
    label: constantToDisplayName(k),
    value: UserRole[k]
  }));
  const [selected, setSelected] = useState([]);
  const [disabledUsers, setDisabledUsers] = useState([]);
  const [nonConflictedUsers, setNonConflictedUsers] = useState([]);
  const {
    users: contractUsers
  } = contract;
  const [barStatus, setBarStatus] = useState('closed');
  const [barError, setBarError] = useState(null);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const {
    data: userList,
    isLoading
  } = useContractSuggestedUserListQuery({
    filters,
    contractId: contract.id,
    page
  }, {
    keepPreviousData: true
  });
  const tableData = useMemo(() => userList?.content ?? [], [userList]);
  const hasError = (errors, error) => {
    if (Array.isArray(error)) {
      return errors.some(e => error.includes(e));
    } else {
      return errors.includes(error);
    }
  };
  useEffect(() => {
    const contractUsers = contract.users;
    setSelected([...contractUsers]);
  }, []);
  const isMultipleUsersInfo = useMemo(() => selected.length > 1 || newUsersList.length > 1, [selected, newUsersList]);
  useEffect(() => {
    if (!contractUsers || !userList) return;
    const checkOverlaps = async () => {
      const disabledUsers = await getUsersContractOverlaps({
        contract: {
          id: contract.id,
          startDate: contract.startDate,
          endDate: contract.endDate,
          type: contract.type,
          products: contract.products.map(p => ({
            id: p.id
          })),
          users: userList.content.map(u => ({
            email: u.email
          }))
        }
      });
      const nonConflictedUsers = userList?.content.filter(user => !disabledUsers.some(el => el.user.email === user.email));
      setDisabledUsers(disabledUsers);
      setNonConflictedUsers(nonConflictedUsers);
    };
    checkOverlaps().catch(e => console.error(e));
  }, [userList]);
  const {
    mutate: setContractUsers,
    ...mutation
  } = useSetContractUsersMutation();
  useEffect(function onUsersAssigned() {
    if (mutation.isSuccess) {
      addToast('Users are assigned to the contract successfully', 'success');
      onAssignSuccess();
    }
  }, [mutation.isSuccess]);
  useEffect(function onMutationError() {
    if (mutation.error) {
      console.error(mutation.error);
      addApiErrorToast(mutation.error, 'Failed to assign users');
    }
  }, [mutation.error]);
  useEffect(() => {
    validateNewUser();
  }, [newUser.email, newUser.role]);
  function handleAssignClick() {
    setContractUsers({
      contractId: contract.id,
      users: selected
    });
  }
  const handleSelectAllUsers = () => {
    const areAllSelected = checkAllSelected();
    areAllSelected ? setSelected([...newUsersList]) : setSelected([...newUsersList, ...nonConflictedUsers]);
  };
  const checkAllSelected = () => {
    const areAllSelected = nonConflictedUsers.every(item => {
      return selected.some(el => el.email === item.email);
    });
    return areAllSelected;
  };
  const validateNewUser = () => {
    const errors = [];
    const isEmailValidated = isEmail(newUser.email);
    if (!newUser.email) {
      errors.push('EMAIL_REQUIRED');
    }
    if (!newUser.role && userRole !== UserRole.Distributor) {
      errors.push('ROLE_REQUIRED');
    }
    if (newUser.email && !isEmailValidated) {
      errors.push('EMAIL_INVALID');
    }
    setUserErrors(errors);
  };
  const updateUsersList = async () => {
    if (userErrors.length > 0) {
      setShowUserErrors(true);
    } else {
      setCheckingEmail(true);
      const isEmailAvailable = await UserApi.isAvailable(newUser.email);
      setCheckingEmail(false);
      if (!isEmailAvailable) {
        if (userRole !== UserRole.Distributor) {
          setBarStatus('emailError');
        } else {
          validateTransfer(newUser.email).then(response => {
            if (response.error) {
              setBarStatus('emailError');
            } else {
              setBarStatus('transfer');
            }
          });
        }
      } else {
        setNewUsersList([...newUsersList, newUser]);
        setSelected([...selected, {
          email: newUser.email,
          role: newUser.role
        }]);
        setNewUser(EMPTY_NEW_USER);
        setShowUserErrors(false);
      }
    }
  };
  const handleInviteToOrganizationError = useCallback(async error => {
    const message = await resolveApiErrorMessage(error, {
      defaultMessage: 'There was a problem with sending the invitation.',
      logErrors: true
    });
    setBarError(message);
    setBarStatus('transferError');
    setIsInvitationModalOpen(false);
  }, []);
  const handleInviteToOrganizationSent = useCallback(() => {
    setBarError(null);
    setBarStatus('success');
    setIsInvitationModalOpen(false);
  }, []);
  function createUserSelectionChangeHandler(user, event) {
    if (event.target.checked) {
      setSelected([...selected, {
        email: user.email,
        role: user.role,
        status: user.status
      }]);
    } else {
      setSelected(selected.filter(item => item.email !== user.email));
    }
  }
  function renderUserContainer(_ref2) {
    let {
      disabled = false,
      user
    } = _ref2;
    return /*#__PURE__*/_jsxs(UserContainer, {
      to: `/users/edit/${encodeURIComponent(user.email)}`,
      "data-sentry-element": "UserContainer",
      "data-sentry-component": "renderUserContainer",
      "data-sentry-source-file": "AssignContractUsers.tsx",
      children: [' ', /*#__PURE__*/_jsx(Checkbox, {
        checked: selected.filter(el => el.email === user.email).length > 0,
        disabled: disabled,
        label: false,
        onChange: ev => createUserSelectionChangeHandler(user, ev),
        "data-sentry-element": "Checkbox",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "Checkbox"
      }), /*#__PURE__*/_jsx(UserStatusDot, {
        $display: "inline-block",
        $mr: "1sp",
        status: user.status,
        "data-sentry-element": "UserStatusDot",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "UserStatusDot"
      }), /*#__PURE__*/_jsx(UserEmail, {
        "data-sentry-element": "UserEmail",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "UserEmail",
        children: user.email
      }), /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        $width: "100u",
        as: "div",
        $justifyContent: "space-between",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "div",
          "data-sentry-element": "Text",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "Text",
          children: constantToDisplayName(user.role)
        }), user.status === undefined ? /*#__PURE__*/_jsx(NewBadge, {
          children: "NEW"
        }) : null]
      }), ' ']
    });
  }
  const selectedUser = userRoles.find(o => o.value === newUser.role);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({
    columns: useMemo(() => [{
      Header: 'Email',
      width: 120,
      accessor: 'email',
      Cell: _ref3 => {
        let {
          value,
          row
        } = _ref3;
        const user = row.original;
        const conflictingContract = disabledUsers?.filter(dUser => dUser.user.email === user.email)[0];
        const disabled = Boolean(conflictingContract);
        return /*#__PURE__*/_jsx(UserLi, {
          $padding: `0`,
          disabled: disabled,
          children: /*#__PURE__*/_jsx(UserRow, {
            children: /*#__PURE__*/_jsxs(UserContainer, {
              children: [/*#__PURE__*/_jsx(Checkbox, {
                checked: selected.filter(el => el.email === user.email).length > 0,
                disabled: disabled,
                onChange: event => createUserSelectionChangeHandler(user, event)
              }), /*#__PURE__*/_jsx(UserStatusDot, {
                $display: "inline-block",
                $mr: "1sp",
                status: row.original.status
              }), value, ' ', disabled && /*#__PURE__*/_jsxs(UserLiInfo, {
                children: [/*#__PURE__*/_jsx(InfoIcon, {
                  size: 1
                }), "Already assigned to conflicting", ' ', /*#__PURE__*/_jsx(Text, {
                  as: Link,
                  variant: "interactive",
                  $display: "inline",
                  to: `/contracts/${conflictingContract.contract.id}`,
                  onClick: () => {
                    onCancel();
                  },
                  children: "contract"
                })]
              })]
            })
          })
        }, user.email);
      }
    }, {
      Header: 'Role',
      accessor: 'role'
    }], [selected, disabledUsers]),
    data: tableData,
    disableMultiSort: true,
    manualSortBy: true
  });
  return /*#__PURE__*/_jsxs(ModalLayout, {
    $width: "900px",
    "data-sentry-element": "ModalLayout",
    "data-sentry-component": "AssignContractUsers",
    "data-sentry-source-file": "AssignContractUsers.tsx",
    children: [/*#__PURE__*/_jsx(ModalHeader, {
      $m: "2sp 5sp 0",
      fontSize: 20,
      fontWeight: 700,
      "data-sentry-element": "ModalHeader",
      "data-sentry-source-file": "AssignContractUsers.tsx",
      "data-sentry-element": "ModalHeader",
      children: "Assign users"
    }), /*#__PURE__*/_jsxs(ContractSummary, {
      "data-sentry-element": "ContractSummary",
      "data-sentry-source-file": "AssignContractUsers.tsx",
      "data-sentry-element": "ContractSummary",
      children: [/*#__PURE__*/_jsxs(ContractDataLabel, {
        "data-sentry-element": "ContractDataLabel",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "ContractDataLabel",
        children: ["Contract ID:", ' ', /*#__PURE__*/_jsx(ContractData, {
          "data-sentry-element": "ContractData",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "ContractData",
          children: contract.contractId ? contract.contractId : '-'
        })]
      }), /*#__PURE__*/_jsxs(ContractDataLabel, {
        "data-sentry-element": "ContractDataLabel",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "ContractDataLabel",
        children: ["Legal name: ", /*#__PURE__*/_jsx(ContractData, {
          "data-sentry-element": "ContractData",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "ContractData",
          children: contract.name
        })]
      }), /*#__PURE__*/_jsxs(ContractDataLabel, {
        "data-sentry-element": "ContractDataLabel",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "ContractDataLabel",
        children: ["Organization", ' ', /*#__PURE__*/_jsx(ContractData, {
          "data-sentry-element": "ContractData",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "ContractData",
          children: contract.organization?.name
        })]
      })]
    }), /*#__PURE__*/_jsx(InviteToOrganizationModal, {
      isOpen: isInvitationModalOpen,
      muteToasts: true,
      organization: contract.organization,
      onError: handleInviteToOrganizationError,
      onInviteSent: handleInviteToOrganizationSent,
      onRequestClose: () => setIsInvitationModalOpen(false),
      readOnly: true,
      userEmail: newUser.email,
      "data-sentry-element": "InviteToOrganizationModal",
      "data-sentry-source-file": "AssignContractUsers.tsx",
      "data-sentry-element": "InviteToOrganizationModal"
    }), /*#__PURE__*/_jsx(ModalContent, {
      "data-sentry-element": "ModalContent",
      "data-sentry-source-file": "AssignContractUsers.tsx",
      "data-sentry-element": "ModalContent",
      children: /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        $justifyContent: "space-between",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsxs(Text, {
          $mb: "2sp",
          fontSize: 14,
          fontWeight: 'bold',
          "data-sentry-element": "Text",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "Text",
          children: ["Users assigned to contract (", selected.length, ")"]
        }), /*#__PURE__*/_jsxs(Text, {
          size: "s",
          "data-sentry-element": "Text",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "Text",
          children: ["Users total: ", userList?.totalElements]
        })]
      })
    }), isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
      $minHeight: "200u"
    }) : userList && /*#__PURE__*/_jsxs(ModalContent
    // $pl="0"
    // $pr="0"
    , {
      $pt: "0sp",
      $mt: "0",
      variant: "scrollable",
      $borderBottom: "none",
      $borderTop: "none",
      children: [isMultipleUsersInfo && /*#__PURE__*/_jsx(Alert, {
        title: '',
        variant: 'info',
        $paddingBottom: "5px",
        children: `Contract's usage limits are shared across all the accounts under
                it.`
      }), ' ', /*#__PURE__*/_jsx(Text, {
        $color: tombac.color('neutral', 700),
        fontSize: 14,
        children: "List of all users of the organization. Select those you want to assign to this contract, unselect if you want to remove users from the contract."
      }), /*#__PURE__*/_jsx(Box, {
        $margin: "1sp auto",
        $maxWidth: "1280u",
        $padding: '0 3sp',
        children: newUsersList.map(user => {
          return /*#__PURE__*/_jsx(UserLi, {
            $padding: `0 5px`,
            children: /*#__PURE__*/_jsx(UserRow, {
              children: renderUserContainer({
                user
              })
            })
          }, user.email);
        })
      }), /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        $margin: "0sp auto"
        // $maxWidth="780u"
        // $padding={'0 3sp'}
        ,
        $justifyContent: "space-between",
        $alignItems: "baseline",
        children: [/*#__PURE__*/_jsx(SearchInput, {
          onChange: e => setFilters({
            ...filters,
            searchValues: e.target.value
          }),
          placeholder: "Search user",
          prepend: /*#__PURE__*/_jsx(SearchIcon, {}),
          type: "text",
          value: filters.searchValues ?? ''
        }), /*#__PURE__*/_jsx(Pagination, {
          $paddingBottom: "2sp",
          page: page,
          itemCount: userList.totalElements,
          onPageChange: page => {
            setPage(page);
          }
        })]
      }), /*#__PURE__*/_jsxs(Box, {
        $boxSizing: "content-box",
        $margin: "0sp auto",
        $maxWidth: "1280u"
        // $padding={'0 3sp'}
        ,
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: () => handleSelectAllUsers(),
          variant: "flat",
          children: checkAllSelected() ? 'Deselect all' : 'Select all'
        }), tableData.length === 0 ? /*#__PURE__*/_jsx(Box, {
          $display: "flex",
          $justifyContent: "center",
          $mt: "3sp",
          children: /*#__PURE__*/_jsx(Text, {
            variant: "secondary",
            children: "No user matches the criteria"
          })
        }) : /*#__PURE__*/_jsxs(TableAuto, {
          ...getTableProps(),
          children: [/*#__PURE__*/_jsx(Thead, {
            children: headerGroups.map(headerGroup => {
              const {
                key: headerGroupKey,
                ...headerGroupProps
              } = headerGroup.getHeaderGroupProps();
              return /*#__PURE__*/_jsx(Tr, {
                ...headerGroupProps,
                children: headerGroup.headers.map(column => {
                  const {
                    key: headerKey,
                    ...headerProps
                  } = column.getHeaderProps();
                  return /*#__PURE__*/_jsxs(Th, {
                    ...headerProps,
                    children: [column.render('Header'), ' ', column.isSorted && (column.isSortedDesc ? /*#__PURE__*/_jsx(ChevronDownIcon, {
                      size: "xs"
                    }) : /*#__PURE__*/_jsx(ChevronUpIcon, {
                      size: "xs"
                    }))]
                  }, headerKey);
                })
              }, headerGroupKey);
            })
          }), /*#__PURE__*/_jsx(Tbody, {
            ...getTableBodyProps(),
            children: rows.map(row => {
              prepareRow(row);
              const {
                key: rowKey,
                ...rowProps
              } = row.getRowProps();
              return /*#__PURE__*/_jsx(Tr, {
                ...rowProps,
                $height: "64u",
                children: row.cells.map(cell => {
                  const {
                    key: cellKey,
                    ...cellProps
                  } = cell.getCellProps();
                  return /*#__PURE__*/_jsx(Td, {
                    $overflow: "hidden",
                    $whiteSpace: "nowrap",
                    ...cellProps,
                    children: cell.render('Cell')
                  }, cellKey);
                })
              }, rowKey);
            })
          })]
        }), /*#__PURE__*/_jsx(Pagination, {
          showItemsPerPage: false,
          $paddingTop: "2sp",
          page: page,
          itemCount: userList.totalElements,
          onPageChange: page => {
            setPage(page);
          }
        })]
      })]
    }), /*#__PURE__*/_jsxs(ModalContent, {
      "data-sentry-element": "ModalContent",
      "data-sentry-source-file": "AssignContractUsers.tsx",
      "data-sentry-element": "ModalContent",
      children: [/*#__PURE__*/_jsx(AddUsersFormLabel, {
        as: "div",
        "data-sentry-element": "AddUsersFormLabel",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "AddUsersFormLabel",
        children: "Add new users to the organizations."
      }), /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        $alignItems: "baseline",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(FormGroup, {
          ...(userRole !== UserRole.Distributor ? {
            $width: '350px'
          } : {
            $width: '100%'
          }),
          $mr: "2sp",
          caption: hasError(userErrors, ['EMAIL_REQUIRED', 'EMAIL_INVALID']) && showUserErrors ? /*#__PURE__*/_jsx(ErrorCaption, {
            children: "Invalid e-mail address"
          }) : '',
          "data-sentry-element": "FormGroup",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "FormGroup",
          children: /*#__PURE__*/_jsx(Input, {
            placeholder: "New user e-mail",
            value: newUser.email,
            invalid: hasError(userErrors, ['EMAIL_REQUIRED', 'EMAIL_INVALID']) && showUserErrors,
            onChange: e => setNewUser({
              ...newUser,
              email: e.currentTarget.value
            }),
            "data-sentry-element": "Input",
            "data-sentry-source-file": "AssignContractUsers.tsx",
            "data-sentry-element": "Input"
          })
        }), userRole !== UserRole.Distributor && /*#__PURE__*/_jsx(FormGroup, {
          caption: hasError(userErrors, 'ROLE_REQUIRED') && showUserErrors ? /*#__PURE__*/_jsx(ErrorCaption, {
            children: "Invalid role"
          }) : '',
          children: /*#__PURE__*/_jsx(Select, {
            options: userRoles,
            placeholder: "ROLE",
            invalid: hasError(userErrors, 'ROLE_REQUIRED') && showUserErrors,
            value: selectedUser ? selectedUser : null,
            onChange: option => setNewUser({
              ...newUser,
              role: option.value
            })
          })
        }), /*#__PURE__*/_jsx(Button, {
          $ml: "auto",
          busy: checkingEmail,
          disabled: checkingEmail,
          variant: "secondary",
          onClick: () => updateUsersList(),
          "data-sentry-element": "Button",
          "data-sentry-source-file": "AssignContractUsers.tsx",
          "data-sentry-element": "Button",
          children: checkingEmail ? 'Checking email...' : 'Add new user'
        })]
      })]
    }), barStatus !== 'closed' && /*#__PURE__*/_jsxs(ModalContent, {
      $w: "100%",
      $ml: "0",
      children: [/*#__PURE__*/_jsx(Alert, {
        isOpen: barStatus === 'emailError',
        variant: "danger",
        title: "E-mail address already in use",
        onRequestClose: () => {
          setBarStatus('closed');
        }
      }), /*#__PURE__*/_jsx(Alert, {
        isOpen: barStatus === 'transfer',
        variant: "alert",
        title: "The user already has trial access",
        icon: /*#__PURE__*/_jsx(AcceptIcon, {
          size: "lg"
        }),
        toolbar: /*#__PURE__*/_jsx(Box, {
          children: /*#__PURE__*/_jsx(Button, {
            variant: "alert",
            size: "xs",
            onClick: () => setIsInvitationModalOpen(true),
            children: "Send an invitation"
          })
        }),
        children: /*#__PURE__*/_jsx(InvitationText, {
          children: "You can transfer the user to your organization by sending an invitation."
        })
      }), /*#__PURE__*/_jsx(Alert, {
        isOpen: barStatus === 'success',
        variant: "success",
        title: "The invitation has been sent to user",
        onRequestClose: () => {
          setBarStatus('closed');
        }
      }), /*#__PURE__*/_jsx(Alert, {
        isOpen: barStatus === 'transferError',
        variant: "danger",
        title: barError
      })]
    }), /*#__PURE__*/_jsxs(ModalActions, {
      $m: "3sp 5sp 4sp",
      "data-sentry-element": "ModalActions",
      "data-sentry-source-file": "AssignContractUsers.tsx",
      "data-sentry-element": "ModalActions",
      children: [/*#__PURE__*/_jsx(Button, {
        onClick: onCancel,
        type: "button",
        variant: "flat",
        "data-sentry-element": "Button",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "Button",
        children: "Close"
      }), /*#__PURE__*/_jsx(Button, {
        busy: mutation.isLoading,
        onClick: handleAssignClick,
        type: "button",
        variant: "primary",
        "data-sentry-element": "Button",
        "data-sentry-source-file": "AssignContractUsers.tsx",
        "data-sentry-element": "Button",
        children: "Save"
      })]
    })]
  });
};
const UserEmail = styled.span.withConfig({
  displayName: "AssignContractUsers__UserEmail"
})(["flex:auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
const UserLi = styled.li.withConfig({
  displayName: "AssignContractUsers__UserLi"
})(["all:initial;display:block;padding:", ";transition:background-color 0.1s ease-out;", ";&:hover{background-color:", ";}"], _ref4 => {
  let {
    $padding
  } = _ref4;
  return tombac.parse($padding);
}, _ref5 => {
  let {
    disabled
  } = _ref5;
  return tombac.text({
    color: disabled ? tombac.color('neutral') : tombac.color.black
  });
}, _ref6 => {
  let {
    disabled
  } = _ref6;
  return disabled ? undefined : tombac.color('neutral', 300);
});
const UserLiInfo = styled.div.withConfig({
  displayName: "AssignContractUsers__UserLiInfo"
})(["", " a{color:", ";}"], tombac.text({
  color: '#ccc'
}), tombac.color('accent', 500));
const UserContainer = styled.label.withConfig({
  displayName: "AssignContractUsers__UserContainer"
})(["display:flex;gap:", ";align-items:baseline;"], tombac.space(2));
const NewBadge = styled.div.withConfig({
  displayName: "AssignContractUsers__NewBadge"
})(["padding:2px 5px;background-color:", ";color:", ";"], tombac.color('accent', 200), tombac.color('accent', 500));
const UserRow = styled.div.withConfig({
  displayName: "AssignContractUsers__UserRow"
})(["padding:5px 0;"]);
const SearchInput = styled(Input).withConfig({
  displayName: "AssignContractUsers__SearchInput"
})(["flex:1;max-width:", ";margin-right:", ";"], tombac.unit(200), tombac.space(2));
const ErrorCaption = styled.span.withConfig({
  displayName: "AssignContractUsers__ErrorCaption"
})(["all:initial;cursor:default;color:rgb(255,0,0);font-family:var(--font-family);font-size:0.85rem;font-weight:400;line-height:1.5;text-rendering:optimizeLegibility;-webkit-font-smoothing:antialiased;"]);
const ModalContent = styled(TombacModalContent).withConfig({
  displayName: "AssignContractUsers__ModalContent"
})(["", " ", ""], tombac.variant('variant', {
  default: css(["margin-left:", ";margin-right:", ";"], tombac.space(5), tombac.space(5)),
  scrollable: css(["padding-left:", ";padding-right:", ";"], tombac.space(5), tombac.space(5))
}), propStyling);
const AddUsersFormLabel = styled(Label).withConfig({
  displayName: "AssignContractUsers__AddUsersFormLabel"
})(["", ";color:", ";margin-bottom:", ";display:block;"], tombac.text(), tombac.color('neutral', 600), tombac.space(1));
const TableAuto = styled(Table).withConfig({
  displayName: "AssignContractUsers__TableAuto"
})(["table-layout:auto;thead{display:none;}tbody tr{height:42px;}"]);
const ContractSummary = styled.div.withConfig({
  displayName: "AssignContractUsers__ContractSummary"
})(["align-items:center;display:flex;padding:", ";flex-wrap:wrap;border-bottom:1px solid ", ";"], tombac.space(1, 5, 1), tombac.color('neutral', 400));
const ContractDataLabel = styled.span.withConfig({
  displayName: "AssignContractUsers__ContractDataLabel"
})(["", " font-size:12px;color:", ";font-family:", ";font-weight:400;margin-right:", ";"], tombac.text(), tombac.color('neutral', 700), tombac.altFontFamily, tombac.space(4));
const ContractData = styled.span.withConfig({
  displayName: "AssignContractUsers__ContractData"
})(["", " color:", ";font-weight:600;"], tombac.text(), tombac.color('neutral', 900));
const InvitationText = styled(Text).withConfig({
  displayName: "AssignContractUsers__InvitationText"
})(["font-size:12px;color:", ";"], tombac.color('neutral', 700));